/**
 * Memoized field engine hook.
 *
 * Caches all fieldEngine derived values so engine functions run only once
 * per `definition.fields` change, not on every render.
 *
 * @internal
 * @module hooks/useFieldEngine
 * @see FMPV-MVWC-E001-S004 R-001 — fieldEngine memoization
 */
import { useMemo } from 'react';
import type { MasterPatternViewDefinition } from '../types/definition.types';
import {
  getAllFields,
  deriveListColumns,
  deriveFormInputs,
  deriveFilterControls,
  deriveQuickFilters,
  deriveSortableFields,
  validateFieldDefinitions,
  type ListColumnDescriptor,
  type FormInputDescriptor,
  type FilterControlDescriptor,
  type QuickFilterDescriptor,
  type SortableFieldDescriptor,
} from '../engine/fieldEngine';

export interface FieldEngineCache {
  listColumns: ListColumnDescriptor[];
  filterControls: FilterControlDescriptor[];
  quickFilters: QuickFilterDescriptor[];
  sortableFields: SortableFieldDescriptor[];
  /** Get form inputs for a specific tab key (or all if undefined). */
  getFormInputs: (tabKey?: string) => FormInputDescriptor[];
}

/**
 * Returns a memoized cache of all field engine outputs derived from
 * `definition.fields`. Recomputes only when `definition.fields` reference changes.
 *
 * @param definition - The master view definition
 * @returns Memoized derived column, input, filter, and sort descriptors
 */
export function useFieldEngine(definition: MasterPatternViewDefinition): FieldEngineCache {
  return useMemo(() => {
    const allFields = getAllFields(definition);
    validateFieldDefinitions(allFields);
    return {
      listColumns: deriveListColumns(allFields),
      filterControls: deriveFilterControls(allFields),
      quickFilters: deriveQuickFilters(allFields),
      sortableFields: deriveSortableFields(allFields),
      getFormInputs: (tabKey?: string) => deriveFormInputs(allFields, tabKey),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definition]);
}
